/**
 * User Activity Tracking Utility
 * Automatically tracks user sessions, page views, actions, and errors
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api/analytics`;

// Throttle/debounce settings to reduce API calls
let pendingEvents = [];
let flushTimeout = null;
const FLUSH_INTERVAL = 5000; // Batch events and send every 5 seconds
const isAdminPage = () => window.location.pathname.startsWith('/admin');

// Generate unique session ID
function generateSessionId() {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);
  return `${timestamp}-${random}`;
}

// Get or create session ID
function getSessionId() {
  let sessionId = sessionStorage.getItem('analyticsSessionId');
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('analyticsSessionId', sessionId);
  }
  return sessionId;
}

// Parse user agent for device info
function getDeviceInfo() {
  const ua = navigator.userAgent;
  
  // Detect device type
  let deviceType = 'desktop';
  if (/Mobile|Android|iPhone|iPad/.test(ua)) {
    deviceType = /iPad|Tablet/.test(ua) ? 'tablet' : 'mobile';
  }
  
  // Detect browser
  let browser = 'Unknown';
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Opera')) browser = 'Opera';
  
  // Detect OS
  let os = 'Unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  
  return {
    deviceType,
    browser,
    os,
    userAgent: ua,
    screenResolution: `${window.screen.width}x${window.screen.height}`
  };
}

// Get current assessment token from URL if available
function getAssessmentToken() {
  const match = window.location.pathname.match(/\/assessment\/([^/]+)/);
  return match ? match[1] : null;
}

// Session tracking state
let sessionStarted = false;
let pageViewCount = 0;
let actionCount = 0;
let errorCount = 0;
let currentPageStartTime = Date.now();

// Start a new session
async function startSession(userInfo = {}) {
  if (sessionStarted) return;
  
  const sessionId = getSessionId();
  const deviceInfo = getDeviceInfo();
  
  try {
    await fetch(`${API}/session/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        user_type: userInfo.type || 'anonymous',
        user_id: userInfo.id || null,
        user_email: userInfo.email || null,
        user_name: userInfo.name || null,
        device_type: deviceInfo.deviceType,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        assessment_token: getAssessmentToken(),
        initial_progress: userInfo.progress || null
      })
    });
    sessionStarted = true;
  } catch (err) {
    console.warn('Analytics: Failed to start session', err);
  }
}

// Track an activity event
async function trackEvent(eventType, eventName, metadata = {}) {
  // Skip tracking on admin pages to reduce load
  if (isAdminPage()) {
    return;
  }
  
  const sessionId = getSessionId();
  const deviceInfo = getDeviceInfo();
  
  // Update counts
  if (eventType === 'page_view') pageViewCount++;
  else if (eventType === 'action') actionCount++;
  else if (eventType === 'error') errorCount++;
  
  try {
    await fetch(`${API}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        event_type: eventType,
        event_name: eventName,
        page_url: window.location.href,
        page_title: document.title,
        duration_seconds: metadata.duration || null,
        metadata: metadata,
        user_agent: deviceInfo.userAgent,
        device_type: deviceInfo.deviceType,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        screen_resolution: deviceInfo.screenResolution,
        assessment_token: getAssessmentToken(),
        section_id: metadata.sectionId || null,
        question_id: metadata.questionId || null,
        error_message: metadata.errorMessage || null,
        error_stack: metadata.errorStack || null
      })
    });
  } catch (err) {
    console.warn('Analytics: Failed to track event', err);
  }
}

// Track page view
async function trackPageView(pageTitle = document.title) {
  // Calculate time spent on previous page
  const timeOnPrevPage = Math.round((Date.now() - currentPageStartTime) / 1000);
  currentPageStartTime = Date.now();
  
  await trackEvent('page_view', pageTitle, { 
    duration: timeOnPrevPage,
    referrer: document.referrer 
  });
}

// Track user action (click, form submission, etc.)
async function trackAction(actionName, metadata = {}) {
  await trackEvent('action', actionName, metadata);
}

// Track error
async function trackError(errorMessage, errorStack = null, metadata = {}) {
  await trackEvent('error', 'Error', {
    errorMessage,
    errorStack,
    ...metadata
  });
}

// Track form interaction
async function trackFormInteraction(sectionId, questionId, interactionType, metadata = {}) {
  await trackEvent('form_interaction', interactionType, {
    sectionId,
    questionId,
    ...metadata
  });
}

// Update session stats
async function updateSession(finalProgress = null) {
  // Skip session updates on admin pages to reduce load
  if (isAdminPage()) {
    return;
  }
  
  const sessionId = getSessionId();
  
  try {
    await fetch(`${API}/session/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        page_views: pageViewCount,
        actions_count: actionCount,
        errors_count: errorCount,
        final_progress: finalProgress
      })
    });
  } catch (err) {
    console.warn('Analytics: Failed to update session', err);
  }
}

// End session
async function endSession(finalProgress = null) {
  const sessionId = getSessionId();
  
  try {
    await fetch(`${API}/session/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        final_progress: finalProgress
      })
    });
  } catch (err) {
    console.warn('Analytics: Failed to end session', err);
  }
}

// Auto-track on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // Use sendBeacon for reliable tracking on page close
    const sessionId = getSessionId();
    navigator.sendBeacon?.(`${API}/session/end`, JSON.stringify({
      session_id: sessionId,
      final_progress: null
    }));
  });
  
  // Track visibility changes (tab switching)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      updateSession();
    }
  });
}

// Export tracking functions
export const analytics = {
  startSession,
  trackPageView,
  trackAction,
  trackError,
  trackFormInteraction,
  updateSession,
  endSession,
  getSessionId
};

export default analytics;
